import { CheckCircle2, Info, TriangleAlert, X } from 'lucide-react'
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'

import { cn } from '../../utils/cn'

type ToastVariant = 'default' | 'success' | 'error'

type ToastOptions = {
  title: string
  description?: string
  variant?: ToastVariant
  duration?: number
}

type Toast = ToastOptions & { id: string }

type ToastContextValue = {
  addToast: (options: ToastOptions) => void
  removeToast: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined)

const generateId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return Math.random().toString(36).slice(2)
}

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([])
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({})

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
    const timer = timers.current[id]
    if (timer) {
      clearTimeout(timer)
      delete timers.current[id]
    }
  }, [])

  const addToast = useCallback((options: ToastOptions) => {
    const id = generateId()
    const duration = options.duration ?? 5000
    setToasts((prev) => [...prev, { ...options, id, duration }])

    if (duration > 0) {
      timers.current[id] = setTimeout(() => removeToast(id), duration)
    }
  }, [removeToast])

  const value = useMemo(() => ({ addToast, removeToast }), [addToast, removeToast])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed bottom-6 right-6 z-50 flex w-full max-w-md flex-col gap-3">
        {toasts.map((toast) => (
          <ToastMessage key={toast.id} toast={toast} onDismiss={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

const ToastMessage = ({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) => {
  const variant = toast.variant ?? 'default'
  const variantStyles: Record<ToastVariant, string> = {
    default: 'border border-border bg-card/95 text-foreground shadow-lg',
    success: 'border border-emerald-200 bg-emerald-50 text-emerald-900 shadow-emerald-100 shadow-lg',
    error: 'border border-rose-200 bg-rose-50 text-rose-900 shadow-rose-100 shadow-lg'
  }

  const Icon = variant === 'success' ? CheckCircle2 : variant === 'error' ? TriangleAlert : Info

  return (
    <div className={cn('pointer-events-auto rounded-2xl px-4 py-3 transition-all', variantStyles[variant])}>
      <div className="flex items-start gap-3">
        <div className="rounded-full bg-white/40 p-1 text-current">
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold leading-tight">{toast.title}</p>
          {toast.description && <p className="text-xs text-muted-foreground/80">{toast.description}</p>}
        </div>
        <button
          type="button"
          aria-label="Dismiss notification"
          className="rounded-full p-1 text-muted-foreground/80 transition hover:bg-black/5"
          onClick={() => onDismiss(toast.id)}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-3 h-1 w-full rounded-full bg-border/70">
        <div className="h-full rounded-full bg-foreground/60" />
      </div>
    </div>
  )
}

export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
